// "Book media": the one booking action on a screen, plus the listing media
// prompt that explains what's missing. The prompt never repeats the button:
// it says why, and the header "Book media" opens booking with that service
// preselected. "Also missing" items are small links into the same flow.
import { useMemo, useState } from "react";
import { F, I, Pill, Btn } from "@/lib/bricks";
import { useAuth } from "@/contexts/AuthContext";
import { dismissGaps, dismissedGaps, fallbackBookHref, openBookMedia, primaryGapFocus, visibleGaps } from "@/lib/listingMedia";
import { useClaimBookingAction } from "@/lib/bookingAction";

const SHORT = { photos: "Photos", visit: "Virtual Visit", video: "Video", floorplan: "Floor plan", drone: "Drone", staging: "Staging" };

function useBook({ dealId, propertyId, summary }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState("");
  const book = async (focus) => {
    if (busy) return;
    setBusy(focus || "any");
    const fallback = fallbackBookHref({
      address: summary?.listing?.address,
      user,
      details: summary?.details,
      focus,
      gaps: (summary?.gaps || []).map((g) => g.id),
      photoReason: (summary?.gaps || []).find((g) => g.id === "photos")?.reason,
    });
    await openBookMedia({ dealId, propertyId, focus, fallback });
    setBusy("");
  };
  return { book, busy, user };
}

/** The listing's one "Book media" action. Preselects the service the prompt names. */
export function BookMediaButton({ dealId, propertyId, summary, dark, full, variant = "accent", label = "Book media", testId = "book-media" }) {
  const { book, busy } = useBook({ dealId, propertyId, summary });
  useClaimBookingAction();
  const focus = primaryGapFocus(summary);
  return (
    <Btn variant={variant} dark={dark} full={full} data-testid={testId} data-focus={focus || ""} disabled={!!busy} onClick={() => book(focus)}>
      {busy ? "Opening…" : label}
    </Btn>
  );
}

/** "12 photos · 9 from a phone" — figures in mono, words in the display face. */
export function PhotoQualityLine({ text, p, style = {} }) {
  if (!text) return null;
  const parts = String(text).split(/(\d[\d,]*)/);
  return (
    <div data-testid="photo-quality-summary" style={{ fontFamily: F.display, fontSize: 12, color: p.ink3, lineHeight: 1.5, overflowWrap: "anywhere", ...style }}>
      {parts.map((t, i) => (/^\d/.test(t)
        ? <span key={i} style={{ fontFamily: F.mono, fontSize: 11.5, color: p.ink2 }}>{t}</span>
        : <span key={i}>{t}</span>))}
    </div>
  );
}

function fmtDay(d) {
  if (!d) return "";
  const dt = new Date(String(d).length <= 10 ? `${d}T12:00:00` : d);
  return Number.isNaN(dt.getTime()) ? "" : dt.toLocaleDateString("en-CA", { month: "short", day: "numeric" });
}

/**
 * What's missing on this listing and why, in words. Booking stays on the
 * header "Book media" (preselected to the first gap); other gaps are small
 * links into the same flow. Quiet when media is complete; shows the booked
 * shoot's status instead when one is on the way. Dismissible per listing.
 */
export function ListingMediaPrompt({ dealId, propertyId, summary, dark, p, onOpenShoot, style = {} }) {
  const { book, busy, user } = useBook({ dealId, propertyId, summary });
  const kind = propertyId ? "shoot" : "deal";
  const id = propertyId || dealId;
  const uid = user?.id || user?.user_id;
  const [off, setOff] = useState(() => dismissedGaps(uid, kind, id));
  const gaps = useMemo(() => visibleGaps(summary?.gaps, off), [summary, off]);

  if (!summary) return null;

  if (summary.state === "booked" && summary.active) {
    const a = summary.active;
    return (
      <div data-testid="listing-media-booked" style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "12px var(--bx-pad)", border: `1px solid ${p.hair}`, borderRadius: 2, background: p.surf2, fontFamily: F.display, ...style }}>
        <I name="cam" size={16} color={p.ink3} />
        <span style={{ fontSize: 13, color: p.ink }}>Shoot booked</span>
        <Pill tone={a.status === "in progress" ? "warn" : "brick"} dark={dark}>{a.status || "booked"}</Pill>
        {fmtDay(a.scheduled_date) ? <span style={{ fontFamily: F.mono, fontSize: 12, color: p.ink3 }}>{fmtDay(a.scheduled_date)}</span> : null}
        {a.package ? <span style={{ fontSize: 13, color: p.ink3 }}>{a.package}</span> : null}
        {a.property_id && onOpenShoot && String(a.property_id) !== String(propertyId || "") ? (
          <button type="button" onClick={() => onOpenShoot(a.property_id)} style={{ marginLeft: "auto", minHeight: 44, padding: "0 4px", background: "none", border: 0, color: p.ink, fontFamily: F.display, fontSize: 13, fontWeight: 500, textDecoration: "underline", textUnderlineOffset: 3, textDecorationColor: p.hairS, cursor: "pointer" }}>
            View shoot
          </button>
        ) : null}
      </div>
    );
  }

  const pqText = summary.photo_quality?.summary;
  if (!gaps.length) {
    return pqText && summary.state !== "booked"
      ? <PhotoQualityLine text={pqText} p={p} style={{ ...style, marginBottom: style.marginBottom ?? 0 }} />
      : null;
  }
  const [first, ...rest] = gaps;

  return (
    <div data-testid="listing-media-prompt" style={{ position: "relative", padding: "var(--bx-pad)", paddingRight: 56, border: `1px solid ${p.hair}`, borderRadius: 2, background: p.surf || p.bg, fontFamily: F.display, ...style }}>
      <button
        type="button"
        aria-label="Dismiss"
        data-testid="listing-media-dismiss"
        onClick={() => { const ids = (summary.gaps || []).map((g) => g.id); dismissGaps(uid, kind, id, ids); setOff(ids); }}
        style={{ position: "absolute", top: 4, right: 4, width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center", background: "none", border: 0, cursor: "pointer" }}
      >
        <I name="x" size={16} color={p.ink3} />
      </button>
      <div style={{ fontSize: 15, fontWeight: 500, color: p.ink, letterSpacing: -0.2 }}>{first.title}</div>
      <div style={{ fontSize: 13, color: p.ink2, marginTop: 4, lineHeight: 1.45 }}>{first.body}</div>
      {pqText ? <PhotoQualityLine text={pqText} p={p} style={{ marginTop: 8 }} /> : null}
      {rest.length ? (
        <div style={{ marginTop: 4, display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 4, fontSize: 13, color: p.ink3 }}>
            <span style={{ marginRight: 4 }}>Also missing</span>
            {rest.map((g, i) => (
              <span key={g.id} style={{ display: "inline-flex", alignItems: "center" }}>
                <button
                  type="button"
                  data-testid={`listing-media-book-${g.id}`}
                  disabled={!!busy}
                  onClick={() => book(g.id)}
                  style={{ minHeight: 44, padding: "0 4px", background: "none", border: 0, color: p.ink, fontFamily: F.display, fontSize: 13, fontWeight: 500, textDecoration: "underline", textUnderlineOffset: 3, textDecorationColor: p.hairS, cursor: "pointer" }}
                >
                  {busy === g.id ? "Opening…" : SHORT[g.id] || g.title}
                </button>
                {i < rest.length - 1 ? <span aria-hidden="true" style={{ color: p.ink4 }}>·</span> : null}
              </span>
            ))}
        </div>
      ) : null}
    </div>
  );
}

/** List-page header booking action (no listing yet): opens www /book with the
 * signed-in agent's own details. Builders pass label "Book a site visit". */
export function BookMediaLink({ dark, href, label = "Book media", testId = "book-media", icon }) {
  useClaimBookingAction();
  return (
    <Btn as="a" href={href} variant="accent" dark={dark} icon={icon} data-testid={testId} style={{ whiteSpace: "nowrap" }}>
      {label}
    </Btn>
  );
}
